import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Landmark, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { money } from '../money';

const CARD = 'bg-white text-slate-800 border-slate-200';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function useBizTax(base) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${base}/accounting/business-tax`).then((r) => setD(r.data)).catch(() => setD(null)); }, [base]);
  return d;
}

export function BusinessTaxOverview({ base, go }) {
  const d = useBizTax(base);
  return (
    <div className="space-y-4 text-slate-800" data-testid="biztax-overview">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Landmark className="h-5 w-5 text-emerald-600" />Business Tax</h2>
      {!d ? <div className="text-slate-500">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['tax-form', 'Tax form', d.tax_form || '—'],
              ['ein', 'EIN / Tax ID', d.ein || 'Not set'],
              ['fy', 'Fiscal year start', MONTHS[(d.fiscal_year_start_month || 1) - 1]],
              ['year', 'Tax year', String(d.year)],
            ].map(([k, l, v]) => (
              <div key={k} className={`rounded-lg border p-3 ${CARD}`} data-testid={`biztax-card-${k}`}>
                <div className="text-xs text-slate-500">{l}</div><div className="text-lg font-semibold">{v}</div>
              </div>
            ))}
          </div>
          <div className={`rounded-lg border p-5 ${CARD}`}>
            <div className="text-lg font-medium mb-3">Estimated taxable income · {d.year}</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded border border-slate-200 p-3"><div className="text-xs text-slate-500 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" />Income</div><div className="text-xl font-semibold tabular-nums" data-testid="biztax-income">{money(d.income)}</div></div>
              <div className="rounded border border-slate-200 p-3"><div className="text-xs text-slate-500 flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5 text-rose-600" />Expenses</div><div className="text-xl font-semibold tabular-nums" data-testid="biztax-expenses">{money(d.expenses)}</div></div>
              <div className="rounded border border-slate-200 p-3 bg-emerald-50"><div className="text-xs text-slate-500">Net taxable</div><div className="text-xl font-semibold tabular-nums" data-testid="biztax-net">{money(d.net)}</div></div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => go('tax_summary')} data-testid="biztax-go-summary"><FileText className="h-4 w-4 mr-1" />View tax summary</Button>
            <Button variant="outline" onClick={() => go('yearend')} data-testid="biztax-go-yearend">Year-end filing</Button>
          </div>
        </>
      )}
    </div>
  );
}

export function TaxSummaryPage({ base }) {
  const d = useBizTax(base);
  return (
    <div className="space-y-4 text-slate-800" data-testid="tax-summary-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-600" />Tax summary</h2>
      {!d ? <div className="text-slate-500">Loading…</div> : (
        <div className={`rounded-lg border overflow-hidden ${CARD} max-w-2xl`}>
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="font-medium">{d.legal_name || 'Company'}</div>
            <div className="text-sm text-slate-500">Tax year {d.year}{d.tax_form ? ` · Form ${d.tax_form}` : ''}</div>
          </div>
          <table className="w-full text-sm" data-testid="tax-summary-table">
            <tbody>
              <tr className="border-b border-slate-100"><td className="p-3">Total income</td><td className="p-3 text-right tabular-nums">{money(d.income)}</td></tr>
              <tr className="border-b border-slate-100"><td className="p-3">Total expenses</td><td className="p-3 text-right tabular-nums">{money(d.expenses)}</td></tr>
              <tr className="font-semibold bg-slate-50"><td className="p-3">Net taxable income</td><td className="p-3 text-right tabular-nums" data-testid="tax-summary-net">{money(d.net)}</td></tr>
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">Computed from the general ledger at request time. For planning only — confirm with your accountant before filing.</div>
        </div>
      )}
    </div>
  );
}
